import {
	createContext,
	type FC,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	useCallback,
	useContext,
	useId,
	useMemo,
	useState,
} from "react";
import { cn } from "../../lib/utils";
import { Icon, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";

type RootProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<HTMLDivElement>;
	children: ReactNode;
	/** Render expanded on first paint. Uncontrolled from then on. Ignored when `open` is set. */
	defaultOpen?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	trackingId?: string;
	testId?: string;
};

type DisclosureContextValue = { open: boolean; toggle: () => void; panelId: string };

const DisclosureContext = createContext<DisclosureContextValue | null>(null);

const useDisclosureContext = (part: string): DisclosureContextValue => {
	const context = useContext(DisclosureContext);
	if (!context) throw new Error(`${part} must be rendered inside <Disclosure>`);
	return context;
};

const Root: FC<RootProps> = ({
	className,
	children,
	defaultOpen = false,
	open: controlledOpen,
	onOpenChange,
	trackingId,
	testId = "disclosure",
	ref,
	...rest
}) => {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const panelId = useId();
	const open = controlledOpen ?? uncontrolledOpen;

	const toggle = useCallback(() => {
		const next = !open;
		if (controlledOpen === undefined) setUncontrolledOpen(next);
		onOpenChange?.(next);
	}, [open, controlledOpen, onOpenChange]);

	const context = useMemo(() => ({ open, toggle, panelId }), [open, toggle, panelId]);

	return (
		<DisclosureContext.Provider value={context}>
			<div
				ref={ref}
				className={cn("overflow-hidden rounded-md border border-bc-default", className)}
				data-testid={testId}
				{...trackingAttrs(trackingId)}
				{...rest}
			>
				{children}
			</div>
		</DisclosureContext.Provider>
	);
};
Root.displayName = "Disclosure";

type SummaryProps = {
	children: ReactNode;
	className?: string;
	trackingId?: string;
	testId?: string;
	ref?: Ref<HTMLButtonElement>;
};

const Summary: FC<SummaryProps> = ({
	children,
	className,
	trackingId,
	testId = "disclosure-summary",
	ref,
}) => {
	const { open, toggle, panelId } = useDisclosureContext("Disclosure.Summary");
	return (
		<button
			ref={ref}
			type="button"
			aria-expanded={open}
			aria-controls={open ? panelId : undefined}
			onClick={toggle}
			className={cn(
				"flex w-full cursor-pointer items-center gap-1.5 bg-surface-light px-2.5 py-2 text-left transition-colors hover:bg-surface-neutral focus-visible:outline-none focus-visible:ring focus-visible:ring-inset focus-visible:ring-brand-accent focus-visible:ring-opacity-30",
				className,
			)}
			data-testid={testId}
			{...trackingAttrs(trackingId)}
		>
			<Icon
				aria-hidden
				color="tertiary"
				size="micro"
				type={open ? "chevron-down" : "chevron-right"}
			/>
			<Text className="min-w-0" color="secondary" type="caption">
				{children}
			</Text>
		</button>
	);
};
Summary.displayName = "Disclosure.Summary";

type PanelProps = {
	children: ReactNode;
	className?: string;
	trackingId?: string;
	testId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Panel: FC<PanelProps> = ({
	children,
	className,
	trackingId,
	testId = "disclosure-panel",
	ref,
}) => {
	const { open, panelId } = useDisclosureContext("Disclosure.Panel");
	if (!open) return null;
	return (
		<div
			ref={ref}
			id={panelId}
			className={cn(
				"flex flex-col divide-y divide-bc-subtle border-t border-bc-default",
				className,
			)}
			data-testid={testId}
			{...trackingAttrs(trackingId)}
		>
			{children}
		</div>
	);
};
Panel.displayName = "Disclosure.Panel";

type RowProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	/** Detail pinned to the row's end — a count, a page, a duration. Never shrinks. */
	trailing?: ReactNode;
	trackingId?: string;
	testId?: string;
};

const Row: FC<RowProps> = ({
	className,
	children,
	trailing,
	trackingId,
	testId = "disclosure-row",
	ref,
	...rest
}) => (
	<div
		ref={ref}
		className={cn("flex min-w-0 items-baseline gap-2 px-2.5 py-[7px]", className)}
		data-testid={testId}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{children}
		{trailing != null && <div className="ml-auto shrink-0 pl-2">{trailing}</div>}
	</div>
);
Row.displayName = "Disclosure.Row";

/**
 * The collapsible detail box: a bordered surface whose tinted summary row toggles a hairline-
 * divided body. Use it for the trail behind a conclusion — provenance, sources, an audit list —
 * where the summary is enough most of the time and the rows are consulted rather than read.
 *
 * The summary owns the chevron and the caption type, so callers hand over a phrase (and usually
 * a count) rather than their own row chrome. Reach for `DefinitionList` with
 * `captionMode="collapsible"` when the thing being disclosed is one field's explanation, and for
 * `Card` when the content is always visible.
 */
export const Disclosure = Object.assign(Root, { Summary, Panel, Row });

export type DisclosureProps = RootProps;
export type DisclosureSummaryProps = SummaryProps;
export type DisclosurePanelProps = PanelProps;
export type DisclosureRowProps = RowProps;
